// Per-role dashboard configuration used by RoleDashboard.vue.
// `field` is the key read from the counts endpoint response.
import { isSuperAdmin } from "@/components/Common/navConfig";

const TEAL = { color: "#13736f", soft: "rgba(19, 115, 111, 0.1)" };
const AMBER = { color: "#d97706", soft: "rgba(245, 158, 11, 0.12)" };
const GREEN = { color: "#059669", soft: "rgba(16, 185, 129, 0.12)" };
const BLUE = { color: "#2563eb", soft: "rgba(59, 130, 246, 0.12)" };

function kpis({ total, pending, camps, approved }) {
  return [
    { label: "Total Applications", icon: "mdi-file-document-multiple-outline", action: "View all", ...TEAL, ...total },
    { label: "Pending Approval", icon: "mdi-clock-outline", action: "Review now", alert: true, ...AMBER, ...pending },
    { label: "Nature Camps", icon: "mdi-pine-tree", action: "View camps", ...GREEN, ...camps },
    { label: "Approved Applications", icon: "mdi-check-decagram-outline", action: "View all", ...BLUE, ...approved },
  ];
}

// Division-scoped roles share the /dfo/counts payload.
function divisionScoped(routes) {
  return {
    countsUrl: "/dfo/counts/",
    graphUrl: "/division/dashboard/graph",
    // The pending page lists everything at the user's seat (/forwarded/apps),
    // so the pending card counts the same applications.
    pendingCountUrl: "/forwarded/apps/count",
    pendingField: "myPending",
    pendingRoute: routes.pending,
    kpis: kpis({
      total: { field: "totalApplications", to: routes.total },
      pending: { field: "myPending", to: routes.pending },
      camps: { field: "totalNatureCamps", to: routes.camps },
      approved: { field: "diviApproved", to: routes.approved },
    }),
    miniStats: [],
  };
}

// Head-office roles share the /counts payload.
function headOffice(routes) {
  return {
    countsUrl: "/counts/",
    graphUrl: "/admin/dashboard/graph",
    pendingField: "superAdminApprovalPending",
    pendingRoute: routes.pending,
    kpis: kpis({
      total: { field: "applicationsCount", to: routes.total },
      pending: { field: "superAdminApprovalPending", to: routes.pending },
      camps: { field: "campCount", to: routes.camps },
      approved: { field: "approvedCount", to: routes.approved },
    }),
    miniStats: [],
  };
}

// Head-office desks whose pending page lists only what is at their seat
// (/forwarded/apps), so the pending card counts the same applications.
function headOfficeDesk(routes) {
  const config = headOffice(routes);
  return {
    ...config,
    pendingCountUrl: "/forwarded/apps/count",
    pendingField: "myPending",
    kpis: config.kpis.map((kpi) =>
      kpi.field === "superAdminApprovalPending" ? { ...kpi, field: "myPending" } : kpi
    ),
  };
}

const dashboards = {
  admin: {
    ...headOffice({
      total: "/AdminBookinsPage",
      pending: "/PendingApplicationsAdmin",
      camps: "/addcamp",
      approved: "/ApprovedApplicationsAdmin",
    }),
    secondaryAction: { label: "Add nature camp", icon: "mdi-plus", to: "/addcamp" },
    // Admin-only endpoint listing every successful payment.
    paymentsUrl: "/naturecamp-completed-list",
    miniStats: [
      { label: "Registered users", field: "userCount", icon: "mdi-account-group-outline", color: "#7c3aed", soft: "rgba(124, 58, 237, 0.1)", to: "/admin-users-list" },
      { label: "Awaiting division approval", field: "divsionapprovalPending", icon: "mdi-office-building-outline", color: "#0891b2", soft: "rgba(8, 145, 178, 0.1)" },
      { label: "Arriving tomorrow", field: "tomorrowApplicationCount", icon: "mdi-calendar-arrow-right", ...TEAL },
      { label: "Cancelled", field: "cancelled", icon: "mdi-close-circle-outline", color: "#e11d48", soft: "rgba(225, 29, 72, 0.1)", to: "/CancelledApplicationAdmin" },
    ],
  },
  division: {
    ...divisionScoped({
      total: "/ApplicationDivision",
      pending: "/DivisionMybooking",
      camps: "/divisionNatureCamp",
      approved: "/ApprovedApplications",
    }),
    secondaryAction: { label: "Nature camps", icon: "mdi-pine-tree", to: "/divisionNatureCamp" },
    miniStats: [
      { label: "Awaiting admin approval", field: "adminPending", icon: "mdi-shield-account-outline", color: "#0891b2", soft: "rgba(8, 145, 178, 0.1)" },
    ],
  },
  clerk: divisionScoped({
    total: "/AllApplicationsClerk",
    pending: "/ClerkHome",
    approved: "/ApprovedApplicationsClerk",
    camps: "/divisionNatureCamp",
  }),
  superint: divisionScoped({
    total: "/AllApplicationsSuperint",
    pending: "/SuperintedentHome",
    approved: "/ApplicationSuperintedent",
  }),
  adminclerk: headOfficeDesk({
    total: "/AllApplicationsCwwclerk",
    pending: "/AdminClerk",
    approved: "/ApprovedApplicationsCwwclerk",
  }),
  adminsuperint: headOfficeDesk({
    total: "/AllApplicationsCwwministerial",
    pending: "/AdminMinisterialhead",
    approved: "/ApprovedApplicationsCwwministerial",
  }),
  dcf: headOfficeDesk({
    total: "/AllApplicationsDcf",
    pending: "/DcfPending",
    approved: "/ApprovedApplicationsDcf",
  }),
};

export function getDashboardConfig(role) {
  const config = dashboards[role] || dashboards.division;
  if (role !== "admin" || !isSuperAdmin()) return config;
  // Super admin has no Pending Applications page; pending is a status filter
  // on the Applications page.
  const pendingRoute = "/AdminBookinsPage?status=pending";
  return {
    ...config,
    pendingRoute,
    kpis: config.kpis.map((kpi) => (kpi.field === "superAdminApprovalPending" ? { ...kpi, to: pendingRoute } : kpi)),
  };
}
